import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { videosApi, type GetVideoStatsResponse } from "@/lib/api/videos";
import { type VideoJob, VideoStatus } from "@/types/video";

export function useVideos() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  
  const [videos, setVideos] = useState<VideoJob[]>([]);
  const [stats, setStats] = useState<GetVideoStatsResponse | null>(null);
  const [total, setTotal] = useState(0);
  
  // Pagination & Filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("all");
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Reset page when search or status changes
  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const fetchVideos = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("No authentication token available");
      }
      
      const [videosRes, statsRes] = await Promise.all([
        videosApi.getVideos(token, page, limit, search, status),
        videosApi.getStats(token)
      ]);
      
      setVideos(videosRes.videos);
      setTotal(videosRes.total);
      setStats(statsRes);
    } catch (err) {
      console.error("Failed to fetch videos:", err);
      // Don't clear previous data on error to avoid flickering if possible, but for now standard error state
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, getToken, page, limit, search, status]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos, refreshTrigger]);

  const refresh = () => setRefreshTrigger(prev => prev + 1);

  return {
    videos,
    stats,
    total,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    status,
    setStatus,
    loading,
    error,
    refresh
  };
}
